/**
 * NovaV2 — Character Bible visual (#392).
 *
 * The five core moods, rendered to the construction specs:
 *   Body        two-piece floating, lunar teal, ~12px gap (scaled)
 *   Face screen deep space rounded rect, ~70% of head, darker bezel
 *   Eyes        signal-blue soft triangles, 25–30% of screen height
 *   Antenna     deep-space stem · orbit-orange ball · ivory orbital ring
 *   Cheeks      coral @50%, emotive states only
 *   Floating    always — idle is a gentle sine hover loop
 *
 * Motion timings follow the bible: idle 3s float · 4s blink; curious
 * 2s tilt; thinking ring spin; happy 2s sway + cheek pulse;
 * has-suggestion 1.2s bounce + antenna glow.
 *
 * Pure CSS animations — no JS timers, cheap enough for chat pins and
 * cards. The canonical, timeline-driven Nova lives in Nova Remote
 * (nova-remote/) — keep the two visually in sync.
 */

const TEAL = 'hsl(var(--lunar-teal, 182 43% 31%))';
const TEAL_DEEP = 'hsl(182 43% 22%)';
const SPACE = 'hsl(36 10% 10%)';
const BLUE = 'hsl(207 60% 78%)';
const ORANGE = 'hsl(var(--orbit-orange, 17 66% 48%))';
const IVORY = 'hsl(var(--cosmic-ivory, 40 41% 93%))';
const CORAL = 'hsl(20 55% 73%)';

export type NovaV2Mood =
  | 'idle'
  | 'thinking'
  | 'curious'
  | 'happy'
  | 'has-suggestion';

export interface NovaV2Props {
  mood: NovaV2Mood;
  size?: number;
  className?: string;
}

/* One <style> block, injected once per document. */
const CSS = `
@keyframes nv2-hover { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes nv2-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
@keyframes nv2-sway { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
@keyframes nv2-tilt { 0%,100% { transform: rotate(0deg) translateX(0); } 50% { transform: rotate(-6deg) translateX(3px); } }
@keyframes nv2-blink { 0%, 92%, 100% { transform: scaleY(1); } 95%, 97% { transform: scaleY(0.08); } }
@keyframes nv2-ring { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes nv2-glow { 0%,100% { opacity: 0.35; } 50% { opacity: 0.9; } }
@keyframes nv2-cheek { 0%,100% { opacity: 0.35; } 50% { opacity: 0.5; } }
@keyframes nv2-eye-scan { 0%,100% { transform: translateX(0); } 40% { transform: translateX(3px); } 80% { transform: translateX(-2px); } }
.nv2-root { display: inline-block; }
.nv2-hover { animation: nv2-hover 3s ease-in-out infinite; }
.nv2-mood-has-suggestion .nv2-hover { animation: nv2-bounce 1.2s ease-in-out infinite; }
.nv2-body { transform-origin: 50% 55%; }
.nv2-mood-happy .nv2-body { animation: nv2-sway 2s ease-in-out infinite; }
.nv2-mood-curious .nv2-body { animation: nv2-tilt 2s ease-in-out infinite; }
.nv2-eyes { transform-origin: 50% 42%; animation: nv2-blink 4s linear infinite; }
.nv2-mood-thinking .nv2-eyes { animation: nv2-blink 4s linear infinite, nv2-eye-scan 3s ease-in-out infinite; }
.nv2-ringwrap { animation: nv2-ring 6s linear infinite; }
.nv2-mood-thinking .nv2-ringwrap { animation-duration: 1.4s; }
.nv2-antenna-glow { animation: nv2-glow 3s ease-in-out infinite; }
.nv2-mood-has-suggestion .nv2-antenna-glow { animation-duration: 1.2s; }
.nv2-cheeks { animation: nv2-cheek 2s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .nv2-root * { animation: none !important; }
}
`;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.dataset.nv2 = '1';
  el.textContent = CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

export function NovaV2({ mood, size = 96, className }: NovaV2Props) {
  injectCss();

  const happyEyes = mood === 'happy';
  const curious = mood === 'curious';
  const thinking = mood === 'thinking';
  const suggesting = mood === 'has-suggestion';
  const showCheeks = happyEyes || suggesting;

  return (
    <span className={`nv2-root nv2-mood-${mood} ${className ?? ''}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        style={{ overflow: 'visible', display: 'block' }}
      >
        <g className="nv2-hover">
          <g className="nv2-body">
            {/* antenna */}
            <g transform="translate(60, 24)">
              <rect x={-1.6} y={-2} width={3.2} height={12} rx={1.6} fill={SPACE} />
              <g className="nv2-ringwrap" style={{ transformOrigin: '0px -6px' }}>
                <ellipse cx={0} cy={-6} rx={11} ry={3.8} fill="none" stroke={IVORY} strokeWidth={1.6} />
              </g>
              <circle cx={0} cy={-6} r={4.4} fill={ORANGE} />
              <circle className="nv2-antenna-glow" cx={0} cy={-6} r={7.5} fill={ORANGE} opacity={0.35} />
            </g>

            {/* head */}
            <rect x={22} y={32} width={76} height={52} rx={19} fill={TEAL} />
            {/* bezel + screen (~70% of head) */}
            <rect x={31} y={40} width={58} height={36} rx={11} fill={TEAL_DEEP} />
            <rect x={33.5} y={42.5} width={53} height={31} rx={9} fill={SPACE} />

            {/* eyes — signal blue soft triangles (25–30% of screen height) */}
            <g className="nv2-eyes">
              {happyEyes ? (
                <g
                  stroke={BLUE}
                  strokeWidth={3.4}
                  strokeLinecap="round"
                  fill="none"
                >
                  <path d="M 42 60 Q 48 52 54 60" />
                  <path d="M 66 60 Q 72 52 78 60" />
                </g>
              ) : (
                <g fill={BLUE}>
                  <path
                    d={`M 42 63 L 48 ${curious ? 54 : 52.5} L 54 63 Q 54 65 52 65 L 44 65 Q 42 65 42 63 Z`}
                    transform={curious ? 'rotate(6 48 60)' : undefined}
                  />
                  <path
                    d={`M 66 63 L 72 ${curious ? 54 : 52.5} L 78 63 Q 78 65 76 65 L 68 65 Q 66 65 66 63 Z`}
                    transform={curious ? 'rotate(-6 72 60)' : undefined}
                  />
                  {(suggesting || thinking) && (
                    <g opacity={suggesting ? 0.5 : 0.25}>
                      <circle cx={48} cy={59} r={7} fill={BLUE} opacity={0.25} />
                      <circle cx={72} cy={59} r={7} fill={BLUE} opacity={0.25} />
                    </g>
                  )}
                </g>
              )}
            </g>

            {/* cheeks — coral @50%, emotive only */}
            {showCheeks && (
              <g className="nv2-cheeks" opacity={0.5}>
                <ellipse cx={38.5} cy={68} rx={4.6} ry={2.8} fill={CORAL} />
                <ellipse cx={81.5} cy={68} rx={4.6} ry={2.8} fill={CORAL} />
              </g>
            )}

            {/* screen reflection */}
            <path
              d="M 36 46 Q 52 42 70 45 L 66 51 Q 50 48 38 51 Z"
              fill="#fff"
              opacity={0.05}
            />

            {/* lower body piece — constant gap */}
            <rect x={32} y={90} width={56} height={16} rx={8} fill={TEAL} />
            <rect x={39} y={94} width={42} height={8} rx={4} fill={TEAL_DEEP} opacity={0.6} />
          </g>
        </g>
      </svg>
    </span>
  );
}

export default NovaV2;
